import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Tag, Facebook, Instagram, Share2, MapPin, Star, Heart, Moon, Music } from 'lucide-react';
import { motion } from 'framer-motion';
import { FacebookShareButton, TwitterShareButton, WhatsappShareButton } from 'react-share';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const KampalaNightlife = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Kampala Nightlife Guide | TravelCord Blog';
  }, []);

  const handleShare = async (platform: string) => {
    const url = window.location.href;
    const title = "Kampala Nightlife Guide: Best Clubs, Bars & Entertainment";

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'instagram':
        alert('Follow us on Instagram @travelcord.guide');
        break;
      default:
        if (navigator.share) {
          try {
            await navigator.share({ title, url });
          } catch (error) {
            console.error('Error sharing:', error);
          }
        }
    }
  };

  const nightlifeSpots = [
    {
      name: "Guvnor",
      description: "Kampala's premier nightclub with multiple rooms and music genres",
      type: "Nightclub",
      bestNight: "Friday & Saturday"
    },
    {
      name: "Cayenne Restaurant & Lounge",
      description: "Upscale venue with great food, cocktails and weekend DJ sets",
      type: "Lounge",
      bestNight: "Thursday & Saturday"
    },
    {
      name: "Bubbles O'Leary's",
      description: "Popular Irish pub with live sports and weekend DJ nights",
      type: "Pub",
      bestNight: "Friday"
    }
  ];

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1829&q=80"
            alt="Kampala Nightlife"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/blog" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Blog
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <div className="flex items-center mb-4 space-x-4">
              <img 
                src="https://imgur.com/YTHr1B6.jpg"
                alt="Azel John Makumbi"
                className="h-12 w-12 rounded-full border-2 border-white"
              />
              <div>
                <h3 className="text-lg font-medium">Azel John Makumbi</h3>
                <p className="text-white/80 text-sm">Nightlife Expert</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="inline-flex items-center bg-purple-500/20 rounded-full px-2.5 py-0.5 text-xs text-purple-400 mb-4 backdrop-blur-sm">
                <Moon className="h-3 w-3 mr-1" />
                Nightlife Guide
              </div>
              
              <h1 className="text-4xl font-bold">Kampala Nightlife Guide: Best Clubs, Bars & Entertainment</h1>
              
              <div className="flex flex-wrap gap-4 text-sm text-white/80">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  April 10, 2025
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  8 min read
                </div>
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  Kampala
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="prose max-w-none">
                <p className="lead text-lg text-gray-600">
                  Kampala comes alive after dark with a vibrant nightlife scene that offers something for everyone. From upscale lounges to energetic nightclubs and cozy pubs, Uganda's capital city knows how to entertain after sunset. This guide will help you navigate Kampala's best nightlife spots for an unforgettable evening out.
                </p>

                <h2>Best Nightlife Districts in Kampala</h2>
                <p>
                  Kampala's nightlife is spread across several key districts, each with its own unique atmosphere:
                </p>
                <ul>
                  <li><strong>Industrial Area</strong> - Home to many of Kampala's most popular nightclubs</li>
                  <li><strong>Acacia Avenue</strong> - Upscale bars and restaurants with a sophisticated crowd</li>
                  <li><strong>Kabalagala</strong> - Known for its diverse international scene and affordable options</li>
                  <li><strong>Kololo</strong> - Exclusive clubs and lounges frequented by Kampala's elite</li>
                </ul>

                <div className="my-8">
                  <img 
                    src="https://images.unsplash.com/photo-1470225620780-dba8ba36b745?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80" 
                    alt="Kampala nightclub scene"
                    className="rounded-lg w-full"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Kampala's nightclubs feature world-class DJs and vibrant dance floors
                  </p>
                </div>

                <h2>Top Nightclubs in Kampala</h2>
                <p>
                  These are the must-visit nightclubs for anyone looking to experience Kampala's legendary party scene:
                </p>

                {nightlifeSpots.map((spot, index) => (
                  <div key={index} className="mb-6">
                    <h3>{spot.name}</h3>
                    <p>{spot.description}</p>
                    <ul>
                      <li><strong>Type:</strong> {spot.type}</li>
                      <li><strong>Best Nights:</strong> {spot.bestNight}</li>
                    </ul>
                  </div>
                ))}

                <h2>Live Music Venues</h2>
                <p>
                  For those who prefer live performances over DJ sets, Kampala offers several excellent venues:
                </p>
                <ul>
                  <li><strong>Ndere Cultural Centre</strong> - Traditional Ugandan music and dance performances</li>
                  <li><strong>Jazzville</strong> - Intimate jazz club featuring local and international artists</li>
                  <li><strong>Big Mike's</strong> - Live bands playing everything from reggae to afrobeat</li>
                </ul>

                <div className="bg-purple-50 p-6 rounded-lg my-8">
                  <h3 className="text-lg font-semibold mb-4">Nightlife Safety Tips</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Use trusted transportation services like Uber or SafeBoda</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Keep valuables secure and be aware of your surroundings</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Consider joining a guided nightlife tour for your first outing</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Drink responsibly and watch your beverages at all times</span>
                    </li>
                  </ul>
                </div>

                <h2>Dress Code and Etiquette</h2>
                <p>
                  Kampala's nightlife venues generally maintain dress codes, especially at upscale establishments:
                </p>
                <ul>
                  <li>Smart casual is the minimum at most clubs (no sportswear or flip-flops)</li>
                  <li>High-end venues may require formal attire, especially on weekends</li>
                  <li>Some clubs have strict door policies - arrive early or make reservations</li>
                  <li>Valid ID is required at most venues - international passports are widely accepted</li>
                </ul>

                <h2>Nightlife Tours and Packages</h2>
                <p>
                  For the ultimate Kampala nightlife experience, consider booking a guided tour:
                </p>
                <ul>
                  <li>VIP club access with no waiting in lines</li>
                  <li>Transportation between venues</li>
                  <li>Local guides who know the best spots</li>
                  <li>Drink packages and special offers</li>
                </ul>
                <p>
                  Check out our <Link to="/Udanga_Nightlife_Ultimate_Guide" className="text-purple-600 hover:underline">Udanga Night Crawling & Club Guide</Link> for comprehensive nightlife packages and booking options.
                </p>

                <div className="my-8">
                  <img 
                    src="https://images.unsplash.com/photo-1566737236500-c8ac43014a67?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80" 
                    alt="Kampala nightlife tour"
                    className="rounded-lg w-full"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Guided nightlife tours offer the safest and most comprehensive experience
                  </p>
                </div>

                <h2>Conclusion</h2>
                <p>
                  Kampala's nightlife scene is diverse, energetic, and constantly evolving. Whether you're looking for sophisticated cocktail lounges, pulsating dance floors, or live music venues, Uganda's capital has something to offer every nocturnal adventurer. For the best experience, consider joining one of our guided nightlife tours to discover the city's after-dark treasures with local experts.
                </p>
              </div>

              {/* Tags */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm text-gray-600 mr-2">Tags:</span>
                  {["Kampala Nightlife", "Uganda Clubs", "Nightlife Guide", "Entertainment", "Night Out"].map((tag, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-purple-100 hover:text-purple-800 transition-colors cursor-pointer"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Share Buttons */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Share this article</h3>
                <div className="flex space-x-4">
                  <FacebookShareButton
                    url={window.location.href}
                    quote="Kampala Nightlife Guide: Best Clubs, Bars & Entertainment"
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Facebook className="h-5 w-5 mr-2" />
                    Facebook
                  </FacebookShareButton>
                  <TwitterShareButton
                    url={window.location.href}
                    title="Kampala Nightlife Guide: Best Clubs, Bars & Entertainment"
                    className="flex items-center px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    Twitter
                  </TwitterShareButton>
                  <WhatsappShareButton
                    url={window.location.href}
                    title="Kampala Nightlife Guide: Best Clubs, Bars & Entertainment"
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Share2 className="h-5 w-5 mr-2" />
                    Share
                  </WhatsappShareButton>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Author Card */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center mb-4">
                    <img 
                      src="https://imgur.com/YTHr1B6.jpg"
                      alt="Azel John Makumbi"
                      className="h-16 w-16 rounded-full"
                    />
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold">Azel John Makumbi</h3>
                      <p className="text-gray-600">Nightlife Expert</p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    Azel has been exploring Kampala's nightlife scene for over 5 years and specializes in connecting travelers with authentic local nightlife experiences.
                  </p>
                  <Link to="/Udanga_Nightlife_Ultimate_Guide" className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors block text-center">
                    View Nightlife Packages
                  </Link>
                </div>

                {/* Table of Contents */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Table of Contents</h3>
                  <nav className="space-y-2">
                    <a href="#districts" className="block text-gray-600 hover:text-purple-600">Nightlife Districts</a>
                    <a href="#clubs" className="block text-gray-600 hover:text-purple-600">Top Nightclubs</a>
                    <a href="#music" className="block text-gray-600 hover:text-purple-600">Live Music Venues</a>
                    <a href="#safety" className="block text-gray-600 hover:text-purple-600">Safety Tips</a>
                    <a href="#dress-code" className="block text-gray-600 hover:text-purple-600">Dress Code & Etiquette</a>
                    <a href="#tours" className="block text-gray-600 hover:text-purple-600">Nightlife Tours</a>
                  </nav>
                </div>

                {/* Related Articles */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Related Articles</h3>
                  <div className="space-y-4">
                    <Link to="/blog/hidden-gems-kampala" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://imgur.com/EzHnSsj.jpg"
                          alt="Hidden Gems in Kampala"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-purple-600">
                            10 Hidden Gems in Kampala You Can't Miss
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">6 min read</p>
                        </div>
                      </div>
                    </Link>
                    <Link to="/blog/kampala-food-guide" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://imgur.com/4kwMnNu.jpg"
                          alt="Kampala Food Guide"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-purple-600">
                            Kampala's Best Street Food Guide
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">8 min read</p>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* CTA */}
                <div className="bg-purple-50 rounded-lg border border-purple-100 p-6">
                  <div className="flex items-center mb-4">
                    <Moon className="h-6 w-6 text-purple-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">Book a Nightlife Tour</h3>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    Experience Kampala's vibrant nightlife with our expert guides. VIP access, transportation, and drinks included!
                  </p>
                  <Link 
                    to="/Udanga_Nightlife_Ultimate_Guide"
                    className="block w-full py-2 bg-purple-600 text-white text-center rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    View Nightlife Packages
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default KampalaNightlife;